import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {Phone, Home, Shield, Droplets, Layers, Sparkles} from 'lucide-react'
import CanonicalTag from "@/components/CanonicalTag"

const ThankYouPage: React.FC = () => {
  const navigate = useNavigate()

  // Inject LocalBusiness schema for this page
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://autowrapsandiego.com/#localbusiness",
      "name": "Mr. Wrap San Diego",
      "description": "Professional paint protection film (PPF), vehicle wraps, ceramic coating, and window tinting in San Diego and North County.",
      "url": "https://autowrapsandiego.com/",
      "telephone": "+1-858-282-8119",
      "email": "mrwrapmatt@gmail.com",
      "priceRange": "$$-$$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2244 South Santa Fe Avenue, Suite A7",
        "addressLocality": "Vista",
        "addressRegion": "CA",
        "postalCode": "92084",
        "addressCountry": "US"
      },
      "areaServed": [
        "San Diego", "Vista", "Oceanside", "Carlsbad", "San Marcos",
        "Escondido", "Encinitas", "Poway", "Del Mar", "La Jolla",
        "Rancho Santa Fe", "North County San Diego"
      ]
    }
    const el = document.createElement("script")
    el.id = "ty-localbusiness-schema"
    el.type = "application/ld+json"
    el.text = JSON.stringify(schema)
    document.head.appendChild(el)
    return () => {
      const existing = document.getElementById("ty-localbusiness-schema")
      if (existing) existing.remove()
    }
  }, [])

  const services = [
    {
      href: "/car-wrap-san-diego",
      icon: <Layers size={28} className="text-cyan-400" />,
      title: "Vehicle Wraps",
      desc: "Full color change & custom vinyl wraps"
    },
    {
      href: "/paint-protection-film-san-diego",
      icon: <Shield size={28} className="text-cyan-400" />,
      title: "Paint Protection Film",
      desc: "Self-healing PPF for all makes and models"
    },
    {
      href: "/window-tint-san-diego",
      icon: <Sparkles size={28} className="text-cyan-400" />,
      title: "Window Tinting",
      desc: "Premium ceramic tint — heat & UV blocking"
    },
    {
      href: "/ceramic-coating-san-diego",
      icon: <Droplets size={28} className="text-cyan-400" />,
      title: "Ceramic Coating",
      desc: "Long-lasting gloss, hydrophobic protection"
    },
  ]

  return (
    <div className="min-h-screen bg-black pt-20">
      <CanonicalTag url="/thank-you" />

      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Check icon */}
          <div
            className="bg-cyan-500/20 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 border-2 border-cyan-500"
            aria-hidden="true"
          >
            <svg
              className="text-cyan-400 w-12 h-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
            Thank You for Contacting Mr. Wrap!
          </h1>
          <p className="text-xl text-gray-300 mb-4 leading-relaxed">
            We've received your request and a team member will reach out to you shortly — usually within a few hours during business hours.
          </p>
          <p className="text-gray-400 text-lg mb-10">
            In the meantime, feel free to call or text us directly if you have any questions.
          </p>

          {/* Primary CTA */}
          <a
            href="tel:858-282-8119"
            className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold py-5 px-10 rounded-xl transition-all transform hover:scale-105 shadow-xl text-lg mb-6"
            id="ty-call-button"
          >
            <Phone size={22} />
            Call or Text: 858-282-8119
          </a>

          <div className="mt-3">
            <p className="text-gray-500 text-sm">
              Monday – Friday 8 AM – 6 PM &nbsp;·&nbsp; Saturday 9 AM – 4 PM &nbsp;·&nbsp; Vista, CA
            </p>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">What Happens Next</h2>
          <ol className="space-y-6">
            {[
              {
                step: "1",
                title: "We Review Your Request",
                body: "Our team reviews the details you submitted and prepares a tailored response for your vehicle and service needs."
              },
              {
                step: "2",
                title: "We Reach Out to You",
                body: "A team member will contact you by phone or email — usually within a few hours on business days — to discuss your project and pricing."
              },
              {
                step: "3",
                title: "Schedule Your Install",
                body: "Once we've aligned on the right package, we'll get your vehicle booked in at our Vista shop for a professional installation."
              },
            ].map(({ step, title, body }) => (
              <li key={step} className="flex gap-5 items-start">
                <div className="bg-cyan-500/20 border border-cyan-500 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-cyan-400 font-bold">{step}</span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-1">{title}</h3>
                  <p className="text-gray-400 leading-relaxed">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Explore Services */}
      <section className="py-16 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-2 text-center">Explore Our Services</h2>
          <p className="text-gray-400 text-center mb-10">
            Learn more about what we offer while you wait to hear from us.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map(({ href, icon, title, desc }) => (
              <a
                key={href}
                href={href}
                className="bg-gray-900 rounded-xl border border-cyan-500/20 hover:border-cyan-500 p-6 transition-all group"
              >
                <div className="mb-4">{icon}</div>
                <h3 className="text-white font-bold text-lg mb-2 group-hover:text-cyan-400 transition">
                  {title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold text-lg transition"
          >
            <Home size={20} />
            Back to Homepage
          </button>
        </div>
      </section>
    </div>
  )
}

export default ThankYouPage
